import { Type } from '@sinclair/typebox';
import { Value } from '@sinclair/typebox/value';

// Why yes these are the same as socket.io
export const EVENT_TYPE = {
  /** Tab needs to send auth */
  CONNECT: 0,
  DISCONNECT: 1,
  EVENT: 2,
  READY: 3,
  CONNECT_ERROR: 4,
  RELOAD: 5,
  AUTH_ERROR: 6,
  REFRESH: 7,
} as const;
export type EVENT_TYPE = (typeof EVENT_TYPE)[keyof typeof EVENT_TYPE];

export const CENTRIFUGE_EVENT_TYPE = {
  SUB: 0,
  UNSUB: 1,
  MESSAGE: 2,
  SUB_CREATED: 3,
  SUB_READY: 4,
} as const;
export type CENTRIFUGE_EVENT_TYPE =
  (typeof CENTRIFUGE_EVENT_TYPE)[keyof typeof CENTRIFUGE_EVENT_TYPE];

const CentrifugeSubMessageSchema = Type.Tuple([
  Type.Literal(EVENT_TYPE.EVENT),
  Type.Literal(CENTRIFUGE_EVENT_TYPE.SUB),
  Type.String(),
]);
export type CentrifugeSubMessage = [
  type: typeof EVENT_TYPE.EVENT,
  subType: typeof CENTRIFUGE_EVENT_TYPE.SUB,
  channel: string,
];

const CentrifugeUnsubMessageSchema = Type.Tuple([
  Type.Literal(EVENT_TYPE.EVENT),
  Type.Literal(CENTRIFUGE_EVENT_TYPE.UNSUB),
  Type.String(),
]);
export type CentrifugeUnsubMessage = [
  type: typeof EVENT_TYPE.EVENT,
  subType: typeof CENTRIFUGE_EVENT_TYPE.UNSUB,
  channel: string,
];

const CentrifugeDataMessageSchema = Type.Tuple([
  Type.Literal(EVENT_TYPE.EVENT),
  Type.Literal(CENTRIFUGE_EVENT_TYPE.MESSAGE),
  Type.String(),
  Type.Any(),
]);
export type CentrifugeDataMessage = [
  type: typeof EVENT_TYPE.EVENT,
  subType: typeof CENTRIFUGE_EVENT_TYPE.MESSAGE,
  channel: string,
  data: unknown,
];

const CentrifugeSubCreatedMessageSchema = Type.Tuple([
  Type.Literal(EVENT_TYPE.EVENT),
  Type.Literal(CENTRIFUGE_EVENT_TYPE.SUB_CREATED),
  Type.String(),
]);
export type CentrifugeSubCreatedMessage = [
  type: typeof EVENT_TYPE.EVENT,
  subType: typeof CENTRIFUGE_EVENT_TYPE.SUB_CREATED,
  channel: string,
];

const CentrifugeSubReadyMessageSchema = Type.Tuple([
  Type.Literal(EVENT_TYPE.EVENT),
  Type.Literal(CENTRIFUGE_EVENT_TYPE.SUB_READY),
  Type.String(),
]);
export type CentrifugeSubReadyMessage = [
  type: typeof EVENT_TYPE.EVENT,
  subType: typeof CENTRIFUGE_EVENT_TYPE.SUB_READY,
  channel: string,
];

const ConnectMessageSchema = Type.Tuple([
  Type.Literal(EVENT_TYPE.CONNECT),
  Type.Union([Type.String(), Type.Null()]),
]);
export type ConnectMessage = [type: typeof EVENT_TYPE.CONNECT, auth: string | null];

const DisconnectMessageSchema = Type.Tuple([Type.Literal(EVENT_TYPE.DISCONNECT)]);
export type DisconnectMessage = [type: typeof EVENT_TYPE.DISCONNECT];

const EventMessageSchema = Type.Union([
  CentrifugeSubMessageSchema,
  CentrifugeUnsubMessageSchema,
  CentrifugeDataMessageSchema,
  CentrifugeSubCreatedMessageSchema,
  CentrifugeSubReadyMessageSchema,
]);
export type EventMessage =
  | CentrifugeSubMessage
  | CentrifugeUnsubMessage
  | CentrifugeDataMessage
  | CentrifugeSubCreatedMessage
  | CentrifugeSubReadyMessage;

const ConnectErrorMessageSchema = Type.Tuple([
  Type.Literal(EVENT_TYPE.CONNECT_ERROR, Type.String()),
]);
export type ConnectErrorMessage = [type: typeof EVENT_TYPE.CONNECT_ERROR, error: string];

const AuthErrorMessageSchema = Type.Tuple([Type.Literal(EVENT_TYPE.AUTH_ERROR), Type.String()]);
export type AuthErrorMessage = [type: typeof EVENT_TYPE.AUTH_ERROR, error: string];

const RefreshMessageSchema = Type.Tuple([Type.Literal(EVENT_TYPE.REFRESH, Type.String())]);
export type RefreshMessage = [type: typeof EVENT_TYPE.REFRESH];

const ReadyMessageSchema = Type.Tuple([Type.Literal(EVENT_TYPE.READY)]);
export type ReadyMessage = [type: typeof EVENT_TYPE.READY];

const ReloadMessageSchema = Type.Tuple([Type.Literal(EVENT_TYPE.RELOAD)]);
export type ReloadMessage = [type: typeof EVENT_TYPE.RELOAD];

const WorkerMessageSchema = Type.Union([
  ConnectMessageSchema,
  DisconnectMessageSchema,
  EventMessageSchema,
  ConnectErrorMessageSchema,
  ReadyMessageSchema,
  ReloadMessageSchema,
  AuthErrorMessageSchema,
  RefreshMessageSchema,
]);
export type WorkerMessage =
  | ConnectMessage
  | DisconnectMessage
  | EventMessage
  | ConnectErrorMessage
  | ReadyMessage
  | ReloadMessage
  | AuthErrorMessage
  | RefreshMessage;

export const isWorkerMessage = (message: unknown): message is WorkerMessage => {
  return Value.Check(WorkerMessageSchema, message);
};

export const isEventMessage = (message: unknown): message is EventMessage => {
  return Value.Check(EventMessageSchema, message);
};
